<template>
  <main-template>
    <v-row>
      <v-col>
        <div class="d-flex justify-space-between align-center">
          <h1>
            <v-btn color="primary" x-small text @click="goBack">
              <v-icon left> fa fa-angle-left </v-icon>
            </v-btn>
            Gerenciar Faltas Justificadas
            <e-help-tooltip
              text="Painel de avaliação de justificativas de faltas de colaboradores."
            />
          </h1>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card outlined>
          <v-tabs
            v-model="tab"
            background-color="grey lighten-4"
            color="primary"
            grow
            @change="trocarAba"
          >
            <v-tab>
              <v-badge
                :content="pendentesCount"
                :value="pendentesCount > 0"
                color="warning"
                overlap
                class="mr-2"
              >
                <v-icon left small>mdi-clock-alert-outline</v-icon>
              </v-badge>
              Pendentes de Avaliação
            </v-tab>
            <v-tab>
              <v-icon left small>mdi-history</v-icon>
              Histórico de Avaliações
            </v-tab>
          </v-tabs>

          <v-card-text>
            <!-- Aba 0: Pendentes -->
            <div v-if="tab === 0">
              <v-data-table
                :headers="headersPendentes"
                :items="faltasPendentes"
                :loading="carregando"
                no-data-text="Nenhuma falta pendente de avaliação no momento."
              >
                <template v-slot:item.colaborador="{ item }">
                  <div class="font-weight-bold">{{ item.colaborador_nome || item.user?.name }}</div>
                  <div class="caption grey--text">
                    {{ item.colaborador_cargo || item.user?.roleName }}
                  </div>
                </template>

                <template v-slot:item.periodo="{ item }">
                  {{ formatarData(item.data_inicio) }}
                  <span v-if="item.data_fim"> até {{ formatarData(item.data_fim) }}</span>
                </template>

                <template v-slot:item.comprovante="{ item }">
                  <v-btn
                    v-if="item.tem_documento || item.documento_path"
                    icon
                    small
                    color="primary"
                    @click.stop="abrirDocumento(item)"
                  >
                    <v-icon>mdi-file-document-outline</v-icon>
                  </v-btn>
                  <span v-else class="caption grey--text">Sem anexo</span>
                </template>

                <template v-slot:item.acoes="{ item }">
                  <v-btn color="primary" small outlined @click="abrirDetalhes(item)">
                    <v-icon left small>mdi-eye-check-outline</v-icon>
                    Avaliar
                  </v-btn>
                </template>
              </v-data-table>
            </div>

            <!-- Aba 1: Histórico -->
            <div v-if="tab === 1">
              <v-data-table
                :headers="headersHistorico"
                :items="faltasHistorico"
                :loading="carregando"
                no-data-text="Nenhum histórico de avaliação encontrado."
              >
                <template v-slot:item.colaborador="{ item }">
                  <div class="font-weight-bold">{{ item.colaborador_nome || item.user?.name }}</div>
                </template>

                <template v-slot:item.periodo="{ item }">
                  {{ formatarData(item.data_inicio) }}
                  <span v-if="item.data_fim"> até {{ formatarData(item.data_fim) }}</span>
                </template>

                <template v-slot:item.status="{ item }">
                  <v-chip
                    :color="statusColor(item.status)"
                    small
                    dark
                    class="text-uppercase font-weight-bold"
                  >
                    {{ item.status }}
                  </v-chip>
                </template>

                <template v-slot:item.data_avaliacao="{ item }">
                  {{ formatarDataHora(item.data_avaliacao) }}
                </template>

                <template v-slot:item.acoes="{ item }">
                  <v-btn color="primary" icon small @click="abrirDetalhes(item)">
                    <v-icon small>mdi-eye</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <falta-justificada-detail-dialog
      v-model="dialogDetalhes"
      :falta="faltaSelecionada"
      @avaliado="onFaltaAvaliada"
    />
  </main-template>
</template>

<script>
import FaltaJustificadaDetailDialog from "@/components/Colaboradores/FaltaJustificadaDetailDialog.vue";

export default {
  name: "GerenciarFaltasJustificadasPage",
  components: {
    FaltaJustificadaDetailDialog,
  },
  data() {
    return {
      tab: 0,
      carregando: false,
      faltasPendentes: [],
      faltasHistorico: [],
      dialogDetalhes: false,
      faltaSelecionada: null,

      headersPendentes: [
        { text: "Colaborador", value: "colaborador" },
        { text: "Período da Ausência", value: "periodo" },
        { text: "Motivo", value: "motivo" },
        { text: "Comprovante", value: "comprovante", sortable: false },
        { text: "Ação", value: "acoes", sortable: false, align: "right" },
      ],
      headersHistorico: [
        { text: "Colaborador", value: "colaborador" },
        { text: "Período", value: "periodo" },
        { text: "Motivo", value: "motivo" },
        { text: "Decisão", value: "status" },
        { text: "Avaliador", value: "avaliado_por_nome" },
        { text: "Data Decisão", value: "data_avaliacao" },
        { text: "Detalhes", value: "acoes", sortable: false, align: "right" },
      ],
    };
  },
  computed: {
    pendentesCount() {
      return this.faltasPendentes.length;
    },
  },
  mounted() {
    this.carregarPendentes();
  },
  methods: {
    goBack() {
      this.$goBack();
    },
    trocarAba(index) {
      if (index === 0) {
        this.carregarPendentes();
      } else {
        this.carregarHistorico();
      }
    },
    async carregarPendentes() {
      try {
        this.carregando = true;
        const res = await this.$services.colaboradorPontosService.listarTodasFaltasJustificadas({
          status: "pendente",
        });
        this.faltasPendentes = Array.isArray(res) ? res : res.data || [];
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregando = false;
      }
    },
    async carregarHistorico() {
      try {
        this.carregando = true;
        const res = await this.$services.colaboradorPontosService.listarTodasFaltasJustificadas({
          status: "historico",
        });
        this.faltasHistorico = Array.isArray(res) ? res : res.data || [];
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregando = false;
      }
    },
    abrirDetalhes(item) {
      this.faltaSelecionada = item;
      this.dialogDetalhes = true;
    },
    async abrirDocumento(item) {
      try {
        await this.$services.colaboradorPontosService.abrirDocumentoFalta(item.id);
      } catch (error) {
        this.$handleError(error);
      }
    },
    onFaltaAvaliada() {
      if (this.tab === 0) {
        this.carregarPendentes();
      } else {
        this.carregarHistorico();
      }
    },
    statusColor(status) {
      switch (status) {
        case "aprovado":
          return "success";
        case "reprovado":
          return "error";
        default:
          return "warning";
      }
    },
    formatarData(val) {
      if (!val) return "";
      const datePart = val.split("T")[0];
      const [ano, mes, dia] = datePart.split("-");
      return `${dia}/${mes}/${ano}`;
    },
    formatarDataHora(val) {
      if (!val) return "";
      return new Date(val).toLocaleString("pt-BR");
    },
  },
};
</script>
